import React, { useEffect, useRef, useState } from 'react';
import PageContainer from '../../components/PageContainer';
import MainContent from '../../components/MainContent';
import PageTitle from '../../components/Common/PageTitle/PageTitle';
import { useNavigate } from 'react-router-dom';
import ActivityLoader from '../../components/Common/ActivityLoader';
import Notification from '../../components/Common/Notification';
import functions from '../../util/functions';
import Pagination from '../../components/Common/Pagination';
import ActionDropdownItem from '../../components/Common/PageTitle/ActionDropdownItem';
import { useVendorStatement } from '../../DataStore/VendorStatementContext';
import { useVendor } from '../../DataStore/VendorContext';
import { useCurrency } from '../../util/SystemVariables';
import AlertDateSelector from '../../components/Report/AlertDateSelector';

const VendorStatementPage = () => {
    const { accountInfo, getStatement, statement, metaData, setCurrentPage, dispatch } = useVendorStatement();
    const { selectedVendor } = useVendor();
    const navigate = useNavigate();
    const statementRef = useRef(); // Ref to enclose statement content for printing
    const currency = useCurrency();
    const [showAlert, setShowAlert] = useState(false);
   

    useEffect(() => {
        if (!accountInfo) {
            navigate('/');
        }else{
           getStatement(true);
        } 
    }, []);

    const handlePrint = () => {
        const printContent = statementRef.current.innerHTML;
        const printWindow = window.open('', '_blank');
        printWindow.document.open();
        printWindow.document.write(`
            <html>
                <head>
                    <title>Account Statement</title>
                    <style>
                        body { font-family: Arial, sans-serif; margin: 20px; }
                        table { width: 100%; border-collapse: collapse; }
                        table, th, td { border: 1px solid #ccc; }
                        th { background-color: #f5f5f5; }
                        th, td { padding: 8px; text-align: left; }
                        .text-right { text-align: right; }
                        .summary-table { width: 50%; float: right; margin-left: 20px; }
                        .content-wrapper { width: 100%; overflow: hidden; }
                    </style>
                </head>
                <body>
                    ${printContent}
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    if (!accountInfo) {
        return <></>;
    }

    const handleConfirmDateRange = async (range) => {
       dispatch({ type: 'dateRange', payload: range });
       await getStatement(true, range);
     };
   


    const PageActions = ()=>(<>
        
        <ActionDropdownItem iconClass="fa fa-calendar-alt" label="Filter Date Range" onClick={() => setShowAlert(true)}/>
        <ActionDropdownItem iconClass="fa fa-sync" label="Refresh" onClick={()=>getStatement(true)}/>
        <ActionDropdownItem iconClass="fa fa-print" label="Print Statement" onClick={handlePrint}/>

        </>)


    return (
     <>
        <PageContainer>
            <MainContent>
                {/* Page Title */}
                <PageTitle
                    title={accountInfo?.account_name || 'Not found'}
                    description={`${accountInfo?.account_number || ''} | Vendor Account Statement`}
                    iconClass="fa fa-university"
                    sticky={true}
                    PageActions={PageActions}
                    Pagination={metaData ? <Pagination
                        metaData={metaData}
                        onPageChange={(page) => setCurrentPage(page)}
                        loadMore={true}
                /> : <></>}
                />

                {/* Statement Content */}
                <div ref={statementRef}>
                    <div className="content-wrapper d-flex">
                        {/* Statement Header */}
                        <div className="header-section" style={{ flex: 3 }}>
                            <h4>Vendor Statement of Account</h4>
                            <p>
                                <strong>Vendor Name:</strong> {selectedVendor?.title || 'Not found'} <br />
                                <strong>Account Name:</strong> {accountInfo?.account_name || 'Not found'} <br />
                                <strong>Account Number:</strong> {accountInfo?.account_number || 'N/A'} <br/>
                                <strong>Currency:</strong> {currency || 'N/A'}

                            </p>
                            {functions.isObject(statement) && statement.summary && ( <p>
                                <strong>Statement Period:</strong>{' '}
                                {statement.summary.opening_date && <>{functions.formatDate(statement.summary.opening_date)} - {functions.formatDate(statement.summary.closing_date)}</>}
                            </p>)}
                        </div>

                        {/* Statement Summary */}
                        {functions.isObject(statement) && statement.summary && (
                            <div className="summary-table" style={{ flex: 1 }}>
                                <table className="table table-bordered">
                                    <tbody>
                                        <tr>
                                            <th>Opening Balance</th>
                                            <td>{functions.numberFormat(statement.summary?.opening_balance || 0)}</td>
                                        </tr>
                                        <tr>
                                            <th>Total Debits</th>
                                            <td>{functions.numberFormat(statement.summary?.total_debits || 0)}</td>
                                        </tr>
                                        <tr>
                                            <th>Total Credits</th>
                                            <td>{functions.numberFormat(statement.summary?.total_credits || 0)}</td>
                                        </tr>
                                        <tr>
                                            <th>Closing Balance</th>
                                            <td>{functions.numberFormat(statement.summary?.closing_balance || 0)}</td>

                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Transactions Table */}
                    <div className="card mb-4">
                        <div className="card-body">
                            {functions.isObject(statement) && (
                                <table className="table table-striped table-bordered">
                                    <thead className="thead-light">
                                        <tr>
                                            <th style={{whiteSpace: 'nowrap'}}>Record Date</th>
                                            <th style={{whiteSpace: 'nowrap'}}>Trans. Date</th>
                                            <th>Reference</th>
                                            <th>Description</th>
                                            <th>Debit</th>
                                            <th>Credit</th>
                                            <th>Balance</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.isArray(statement.transactions) ? (
                                            statement.transactions.map((transaction, index) => (
                                                <tr key={index}>
                                                    <td style={{whiteSpace: 'nowrap'}}>{transaction.created_at}</td>
                                                    <td style={{whiteSpace: 'nowrap'}}>{functions.formatDate(transaction.transaction_date)}</td>
                                                    <td>{transaction.transaction_ref || '-'}</td>
                                                    <td>{transaction.description || '-'}</td>
                                                    <td>{transaction.debit > 0 ? functions.numberFormat(transaction.debit) : ''}</td>
                                                    <td>{transaction.credit > 0 ? functions.numberFormat(transaction.credit) : ''}</td>
                                                    <td>{functions.numberFormat(transaction.running_balance)}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="text-center">
                                                    No transactions available for this period.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            )}

                            {statement === '404' && (
                                <Notification type="info" message="Statement results not found" closable={false} />
                            )}
                            {!statement && <ActivityLoader />}
                        </div>
                    </div>
                </div>

               
            </MainContent>
        </PageContainer>
         <AlertDateSelector
            show={showAlert}
            onClose={() => setShowAlert(false)}
            onConfirm={handleConfirmDateRange}
        />
        </>
    );
};

export default VendorStatementPage;
